import { ChallengeLocation, GetChallengeResponse, GetPointResponse, IntegrationException, Location, LocationClueDetails } from '../types';
import { LitElement, css, html } from 'lit';
import challengeData, { challengeLocationsDisplayName } from '../helpers/state/challenge-data';
import { customElement, property, state } from 'lit/decorators';
import { delay, isDefined, undefinedIfEmpty } from '../helpers/utils';

import { StateController } from '../helpers/state/state-management/state-controller';
import { areLocationsSame } from '../helpers/filters';
import { escapeCsvValue } from '../helpers/files';
import { getMainPage } from '../helpers/page-name';
import { highStreetCluesAndCodes } from '../helpers/constants';
import { makeColorsIntoNumberSvg } from '../helpers/colors';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';

// Mirrors the server's comparison form: trimmed, lowercased, spacing collapsed.
function normalizeCheckInCode(code: string): string {
    return code.trim().replace(/\s+/g, ' ').toLowerCase();
}

@customElement('assign-challenge-checkins-modal')
export class AssignChallengeCheckinsModal extends LitElement {

    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindChallengeDataState = new StateController(this, challengeData);


    @property({ type: Array })
    data: ChallengeLocation[] = [];

    @state()
    allLocations: Array<Location | undefined> = [];

    @state()
    challenge: GetChallengeResponse | undefined;

    @state()
    challengeLocations: ChallengeLocation[] = [];

    @state()
    isValidCheckinCode: boolean[] = [];

    @state()
    codeRequired: boolean = true;

    @state()
    needsSave: boolean = false;

    @state()
    locationsOnLoad: number = 0;

    @state()
    numChangedLocations: number = 0;

    @state()
    activeEditIndex: number = -1;

    async firstUpdated() {
        this.startup();
    }

    async updated(changedProperties: any) {
        changedProperties.forEach((oldValue: any, propName: any) => {
            if(propName === 'data' || propName === 'challenge') {
                this.startup();
            }
        });
    }

    get challengeType() {
        return this.challenge?.challenge_type ?? '';
    }

    private startup() {
        // wait until parent page is ready
        if (!this.challenge || !pointCache.isReady|| !topicData.isReady || !challengeData.isReady) {
            return;
        }
        this.challengeLocations = [];
        this.isValidCheckinCode = [];
        this.allLocations = pointCache.getSortedPoints(undefined, true).filter((p) => p.active).map(p => p.location).filter((item, index, self) => {
            return index === self.findIndex(
                obj => obj.place_id === item.place_id
                && obj.latLng.lat === item.latLng.lat
                && obj.latLng.lng === item.latLng.lng
            );
          });
        // be very careful changing this here. We don't want to accidentally overwrite the check-in codes or qr codes

        this.allLocations.forEach((location, index) => {
            const challengeLocation = this.data.find(
                (c) => areLocationsSame(c.location, location)
            );
            // push the location or create a new one if it doesn't exist
            const isHighStreet = topicData.hasExceptions === IntegrationException.HighStreetSafari;
            let point: GetPointResponse | undefined;
            let highStreetClueAndCode;
            if (isHighStreet) {
                point = pointCache.getPointsFromLocation(location)?.[0];
                // First, try to match by point number
                if (point?.number) {
                    highStreetClueAndCode = highStreetCluesAndCodes.find((element) =>
                        element.number === point?.number
                    );
                }
                
                // If no match by number, get next available code
                if (!highStreetClueAndCode) {
                    highStreetClueAndCode = highStreetCluesAndCodes.find((element) =>
                        !this.challengeLocations.some((loc) => loc.check_in_code === element.code) &&
                        !this.data.some((loc) =>
                            loc.check_in_code === element.code &&
                            this.allLocations.some(al => areLocationsSame(al, loc.location))
                        )
                    );
                }
            }
            this.challengeLocations.push(challengeLocation ?? {
                    _id: undefined,
                    is_active: false,
                    location,
                    value: 1,
                    check_in_code: isHighStreet ? highStreetClueAndCode?.code ?? this.generateConfirmCode() : this.generateConfirmCode(),
                    qr_code_key: undefined,
                    can_geofence_checkin: true,
                    allow_multiple_visits: false,
                    visit_cadence: undefined,
                    max_visits: undefined,
                    clue_details: isHighStreet ? {
                        clue: point?.name ?? '',
                        hint: point?.description ?? '',
                        image_url: isHighStreet ? highStreetClueAndCode?.clueImage ?? '' : '',
                    } : undefined,
                });
                this.isValidCheckinCode.push(true);
            }
        );

        if (this.challengeType === 'hunt' && this.challenge?.scavenger_hunt_settings?.display_one_clue_at_a_time === true && this.challenge?.locations.length > 0) {
            // sort the locations by the order they are in the challenge b/c order matters
            const orderedLocations: ChallengeLocation[] = [];

            // First, add existing challenge locations in their current order
            this.challenge.locations.forEach((challengeLoc) => {
                const matchingLocation = this.challengeLocations.find((l) =>
                    areLocationsSame(l.location, challengeLoc.location)
                );
                if (matchingLocation) {
                    orderedLocations.push(matchingLocation);
                }
            });

            // Then, add any new locations that aren't in the challenge yet
            this.challengeLocations.forEach((challengeLoc) => {
                const alreadyAdded = orderedLocations.some((l) =>
                    areLocationsSame(l.location, challengeLoc.location)
                );
                if (!alreadyAdded) {
                    orderedLocations.push(challengeLoc);
                }
            });

            // Update the arrays
            this.challengeLocations = orderedLocations;
            this.allLocations = orderedLocations.map((location) => location.location);
        }

        this.locationsOnLoad = this.challenge?.locations.filter(location => location.is_active).length ?? 0;
        this.requestUpdate();
    }

    getNumChangedLocations () {
        const locations = this.locationsOnLoad - this.getUniqueLocations(this.challengeLocations.filter((location) => location.is_active)).length;
        this.numChangedLocations = locations > 0 ? locations : locations * -1;
    }

    getUniqueLocations(challenegeLocations: ChallengeLocation[]) {
        const uniqueLocations = challenegeLocations
        .map((p) => p.location)
        .filter((item, index, self) => {
            return (
                index ===
                self.findIndex(
                    (obj) =>
                        areLocationsSame(obj, item)
                )
            );
        });
        return uniqueLocations;
    }

    generateConfirmCode() {
        // make sure this is unique
        const existingCheckinCodes = this.existingCheckinCodes;
        let code = `${Math.floor(1000 + Math.random() * 9000)}`;
        while (existingCheckinCodes.includes(code)) {
            code = `${Math.floor(1000 + Math.random() * 9000)}`;
        }
        return code;
    }

    async makeAllActive() {
        if (this.activeEditIndex !== -1) {
            return;
        }
        this.challengeLocations.forEach((challengeLocation) => {
            challengeLocation.is_active = true;
        });
        await this.handleClickSave();
        this.requestUpdate();
    }

    get allActiveLocationsInOrder() {
        return this.challengeLocations.filter((location) => location.is_active);
    }


    async moveLocationUp(index: number, toLastActive?: boolean) {
        if (index === 0) {
            return;
        }

        if (toLastActive === true) {
            let index = this.challengeLocations.findIndex((location) => !location.is_active);
            if (index === -1) {
                index = 0;
            }
            const temp = this.challengeLocations[index];
            this.challengeLocations[index] = this.challengeLocations[index - 1];
            this.challengeLocations[index - 1] = temp;
        } else {
            const temp = this.challengeLocations[index];
            this.challengeLocations[index] = this.challengeLocations[index - 1];
            this.challengeLocations[index - 1] = temp;
        }

        // update all locations to reflect the new order
        if (this.challengeType === 'hunt') {
            const updatedLocationOrder = this.challengeLocations.map((location) => location.location);
            this.allLocations = updatedLocationOrder;
        }

        await this.handleClickSave();

        this.requestUpdate();
    }

    async moveLocationDown(index: number, toEndOfActive?: boolean) {
        if (index === this.challengeLocations.length - 1) {
            return;
        }
        if (toEndOfActive === true) {
            let index = this.challengeLocations.findIndex((location) => !location.is_active);
            if (index === -1) {
                index = this.challengeLocations.length - 1;
            }
            const temp = this.challengeLocations[index];
            this.challengeLocations[index] = this.challengeLocations[this.challengeLocations.length - 1];
            this.challengeLocations[this.challengeLocations.length - 1] = temp;
        } else {
            const temp = this.challengeLocations[index];
            this.challengeLocations[index] = this.challengeLocations[index + 1];
            this.challengeLocations[index + 1] = temp;
        }

        // update this.allLocations to reflect the new order
        if (this.challengeType === 'hunt') {
            const updatedLocationOrder = this.challengeLocations.map((location) => location.location);
            this.allLocations = updatedLocationOrder;
        }

        await this.handleClickSave();

        this.requestUpdate();
    }

    private handleClickDownloadQrCodes() {
        // send event to parent to download qr codes
        this.dispatchEvent(
            new CustomEvent('on-download-qr-codes', {
                detail: {
                    data: this.challengeLocations,
                }
            })
        );
    }

    private handleClickDownload() {
        // TO DO update this with location summary so we can share more data
        let content = '';
        const read_link = topicData.topic?.cc_read_link ?? topicData.topic?.cc_keyless_link ?? '';

        content +=
            'Location Id,"Active","Challenge Participant","Point Check In Value","Participant Confirmation Code","Unique Location Link"';

        if (this.challenge?.scavenger_hunt_settings) {
            content += `"Order","Clue","Hint",`;
        }

        content += '\n';

        this.challengeLocations.forEach((challengeLocation, index) => {
            content += `${challengeLocation._id?.$oid ?? ''},${escapeCsvValue(challengeLocation.is_active ? 'true' : 'false')},${escapeCsvValue(challengeLocation.location?.search ?? '')},${escapeCsvValue((challengeLocation.value ?? '').toString())},${escapeCsvValue((challengeLocation.check_in_code ?? '').toString())}`;
            content += `,${escapeCsvValue(`${read_link}` + `?challenge_location_id=${challengeLocation._id?.$oid ?? ''}`)}`;

             if (this.challenge?.scavenger_hunt_settings) {
                const order = this.allActiveLocationsInOrder.findIndex((l) => areLocationsSame(l.location, challengeLocation.location)) + 1;
                content += `${escapeCsvValue(order.toString())},${escapeCsvValue(challengeLocation.clue_details?.clue ?? '')},${escapeCsvValue(challengeLocation.clue_details?.hint ?? '')},`;
            }

            content += '\n';
        });



        const filename = 'challenge_checkins.csv';

        const blob = new Blob([content], {
            type: 'text/plain;charset=utf-8',
        });

        this.saveAs(blob, filename);
    }

    saveAs(blob: Blob, fileName: string) {
        const a = document.createElement('a');
        document.body.appendChild(a);
        a.style.display = 'none';

        const url = window.URL.createObjectURL(blob);
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    async handleClickSave() {
        this.dispatchEvent(
            new CustomEvent('on-change-challenge-locations', {
                detail: {
                    data: this.challengeLocations,
                }
            })
        );
        this.needsSave = false;
        await delay(4000);
        this.startup();
        this.requestUpdate();
    }

    async onChangeChallengeLocationValue(e: Event, index: number) {
        const value = (e.target as HTMLInputElement).value ?? '' ;
        const parsedValue = value ? parseInt(value) : 1;
        this.challengeLocations[index].value = parsedValue > 0 ? parsedValue : 1;

        await this.handleClickSave();

        await this.requestUpdate();
    }

    async onChangeChallengeLocationCheckinCode(e: Event, index: number) {
        const inputEl = (e.target as HTMLInputElement);
        if (!isDefined(inputEl)) {
            return;
        }
        const check_in_code = inputEl.value.trim();
        // 2-25 chars, letters/numbers with spaces allowed between words
        const pattern = /^[\p{L}\p{N}][\p{L}\p{N} ]{0,23}[\p{L}\p{N}]$/u;
        // Codes match case-insensitively with spacing collapsed at check-in, so uniqueness must too
        const otherCodes = this.challengeLocations
            .filter((_, i) => i !== index)
            .map((location) => normalizeCheckInCode(location.check_in_code ?? ''));
        const isUnique = !otherCodes.includes(normalizeCheckInCode(check_in_code));
        const isValid = pattern.test(check_in_code) && isUnique;
        if (isValid) {
            inputEl.setCustomValidity('');
        } else {
            if (!isUnique) {
                inputEl.setCustomValidity('This code is already in use. Please enter a unique code.');
                getMainPage().errorToastElement.showToastAndThrow(
                    'This code is already in use. Please enter a unique code.'
                );
            } else {
                inputEl.setCustomValidity('Codes can be a number, a word, or a short phrase — 2-25 letters, numbers, and spaces.');
                getMainPage().errorToastElement.showToastAndThrow(
                    'Codes can be a number, a word, or a short phrase — 2-25 letters, numbers, and spaces.'
                );
            }
        }
        this.isValidCheckinCode[index] = isValid;
        this.challengeLocations[index].check_in_code = check_in_code;

        await this.handleClickSave();

        this.requestUpdate();
    }

    async onChangeChallengeLocationAllowGeofenceCheckin(index: number) {
        this.challengeLocations[index].can_geofence_checkin = !(this.challengeLocations[index].can_geofence_checkin ?? true);

        await this.handleClickSave();

        this.requestUpdate();
    }

    async toggleChallengeLocationIsActive(index: number) {
        // if currently editing another location, don't allow toggling
        if (this.activeEditIndex !== -1) {
            return;
        }
        this.challengeLocations[index].is_active = !this.challengeLocations[index].is_active;

        // if this is a hunt, we need to update the order of the locations and move this location to the end
        if (this.challengeType === 'hunt' && !this.challengeLocations[index].is_active) {
            await this.handleClickSave();
            await this.moveLocationDown(index, true);
        } else if (this.challengeType === 'hunt' && this.challengeLocations[index].is_active) {
            await this.handleClickSave();
            await this.moveLocationUp(index, true);
        } else {
            await this.handleClickSave();
        }

        this.requestUpdate();
    }

    get existingCheckinCodes() {
        return this.challengeLocations
            .map((location) => location.check_in_code)
            .filter((item, index, self) => self.indexOf(item) === index);
    }

    async setChallengeLocationCodeToEmpty(index: number) {
        if (this.challengeLocations[index].check_in_code === '') {
            this.challengeLocations[index].check_in_code = this.generateConfirmCode();
            this.isValidCheckinCode[index] = true;
        } else {
            this.challengeLocations[index].check_in_code = '';
            this.isValidCheckinCode[index] = true;
        }

        await this.handleClickSave();

        this.requestUpdate();
    }

    async setChallengeLocationClueDetails(index: number, value: string) {
        const details: LocationClueDetails = this.challengeLocations[index].clue_details ?? {
            clue: '',
            hint: '',
            image_url: '',
        };
        details.clue = value;
        this.challengeLocations[index].clue_details = details;

        await this.handleClickSave();

        this.requestUpdate();
    }

    async setChallengeLocationHintDetails(index: number, value: string) {
        const details: LocationClueDetails = this.challengeLocations[index].clue_details ?? {
            clue: '',
            hint: '',
            image_url: '',
        };
        details.hint = value;
        this.challengeLocations[index].clue_details = details;

        await this.handleClickSave();

        this.requestUpdate();
    }

    async setChallengeLocationImageUrl(index: number, value: string) {
        const details: LocationClueDetails = this.challengeLocations[index].clue_details ?? {
            clue: '',
            hint: '',
            image_url: '',
        };
        details.image_url = value;
        this.challengeLocations[index].clue_details = details;

        await this.handleClickSave();

        this.requestUpdate();
    }

    async downloadQRCodeLink(index: number) {
        const location = this.challengeLocations[index];
        if (!isDefined(location) || !location.is_active) {
            return;
        }

        const { fileName, blobDataUrl } = await challengeData.downloadQrCodeForLocation(location._id?.$oid ?? '');

        // if there was an error, don't proceed
        if (!isDefined(blobDataUrl) || blobDataUrl === '') {
            getMainPage().errorToastElement.showToastAndThrow(
                'There was an error generating the QR Code. Please try again later or contact Proxi.'
            );
            return;
        }

        const a = document.createElement('a');
        document.body.appendChild(a);
        a.style.display = 'none';

        a.href = blobDataUrl;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(blobDataUrl);
    }

    get clueName() {
        if (this.challengeType === 'hunt' || this.challengeType === 'trivia') {
            return 'Clue';
        } else if (this.challengeType === 'treasure') {
            return 'Stop Name';
        }
        return 'Clue';
    }

    get hintName() {
        if (this.challengeType === 'hunt' || this.challengeType === 'trivia') {
            return 'Hint';
        } else if (this.challengeType === 'treasure') {
            return 'Clue';
        }
        return 'Hint';
    }

    get challengeCheckInVerificationRequired() {
        return this.challenge?.should_verify_check_ins ?? true;
    }

    get challengeCodeVerificationEnabled() {
        return this.challenge?.enable_code_checkin ?? true;
    }

    get challengeQRVerificationEnabled() {
        return this.challenge?.enable_qr_checkin ?? true;
    }

    get challengeGeofenceVerificationEnabled() {
        return isDefined(this.challenge?.geofence_radius_m) && (this.challenge?.geofence_radius_m ?? 0) > 0;
    }

    get challengePhotoCheckinEnabled() {
        return this.challenge?.enable_photo_checkin ?? false;
    }

    get challengeReceiptCheckinEnabled() {
        return this.challenge?.enable_receipt_checkin ?? false;
    }

    render() {
        const hasInvalidCheckinCode = this.isValidCheckinCode.some((v) => !v);
        this.getNumChangedLocations();

        return html`
            <settings-card header="Manage Participating Places" .maxWidth=${'100%'} style="flex: 4;">
                <p class="${this.locationsOnLoad === 0 && this.numChangedLocations === 0 ? 'redText' : ''} info-paragraph"> You <i>currently</i> have <b>${this.locationsOnLoad}</b> participating places and <b>${this.allLocations.length - this.locationsOnLoad}</b> inactive places.
                <div class="modalContent">
                    <div class="modal__content">
                        <div class="challenges-wrapper">
                            <div class="challenge-row__header">
                                ${this.challengeType === 'hunt' && this.challenge?.scavenger_hunt_settings?.display_one_clue_at_a_time === true ? html`<div class="participant">${challengeLocationsDisplayName(challengeData.challenge, false, 'title')} Order</div>` : ''}
                                <div class="participant"><span>Active<br><a @click="${this.makeAllActive}" style="cursor: pointer;text-decoration: underline;">
                                    Make All Active</a></span>
                                </div>
                                <div class="point">Location</div>
                                ${this.challengeType === 'hunt' || this.challengeType === 'treasure' || this.challengeType === 'trivia' ? html`<div class="clue">${this.clueName}</div>` : ''}
                                ${this.challengeType === 'hunt' || this.challengeType === 'treasure' || this.challengeType === 'trivia' ? html`<div class="clue">${this.hintName}</div>` : ''}
                                ${this.challengeType !== 'guide' ? html`
                                    <div class="point">Verification Methods</div>
                                    <div class="checkin-value">Point Check In Value</div>
                                    ${this.challengeCheckInVerificationRequired && this.challengeCodeVerificationEnabled ? html`<div class="checkin-value ">Code</div>` : ''}
                                    ${this.challengeCheckInVerificationRequired && this.challengeQRVerificationEnabled ? html`<div class="confirm-code">QR Code</div>` : ''}
                                    <div class="checkin-value">
                                        </div>
                                ` : ''}
                            </div>
                            ${this.allLocations?.map((location, index) => {
                                const challengeLocation = this.challengeLocations[index];
                                if (!isDefined(challengeLocation)) {
                                    return html``;
                                }
                                const points = pointCache.getPointsFromLocation(location) ?? [];
                                if (points.length === 0) {
                                    return html``;
                                }
                                const order_index = this.allActiveLocationsInOrder.findIndex((l) => areLocationsSame(l.location, location));
                                let available_methods_string = ''; // Use let instead of const
                                if (!this.challengeCheckInVerificationRequired || challengeLocation.check_in_code === '') {
                                    available_methods_string = 'No Verification Required'; // Assign directly
                                } else {
                                    const methods = [];
                                    if (challengeLocation.check_in_code !== '' && this.challengeCodeVerificationEnabled) {
                                        methods.push('Confirmation Code');
                                    }
                                    if (challengeLocation.qr_code_key !== '' && this.challengeQRVerificationEnabled) {
                                        methods.push('QR Code');
                                    }
                                    if (challengeLocation.can_geofence_checkin === true && this.challengeGeofenceVerificationEnabled) {
                                        methods.push('GeoFence');
                                    }
                                    if (this.challengePhotoCheckinEnabled) {
                                        methods.push('Photo Check In');
                                    }
                                    if (this.challengeReceiptCheckinEnabled) {
                                        methods.push('Receipt Check In');
                                    }
                                }
                                return html`
                                    <div class="challenge-row ${this.activeEditIndex === index ? 'active_row' : ''}">
                                        ${this.challengeType === 'hunt' && this.challenge?.scavenger_hunt_settings?.display_one_clue_at_a_time === true ? html`
                                            ${order_index === -1 ? html`<div class="participant">-</div>` : html`<div class="participant">
                                                    ${this.activeEditIndex === -1 ? html `<mwc-icon class="arrow_icon" @click="${async () => await this.moveLocationUp(index)}">arrow_upward</mwc-icon>` : ''}
                                                    ${this.activeEditIndex === -1 ? html `<mwc-icon class="arrow_icon" @click="${async () => await this.moveLocationDown(index)}">arrow_downward</mwc-icon>` : ''}
                                                    <img src="${makeColorsIntoNumberSvg(topicData.backgroundColor, (order_index + 1).toString())}" alt="Order" style="width: 20px; height: 20px;"/>
                                                </div>`}
                                        ` : ''}
                                        <div class="participant">
                                            <form-checkbox
                                                ?disabled="${this.activeEditIndex !== -1}"
                                                id="active"
                                                name="active"
                                                ?checked="${challengeLocation.is_active}"
                                                @click="${async () => await this.toggleChallengeLocationIsActive(index)}"
                                                >
                                            </form-checkbox>
                                        </div>
                                        <div class="point"  @click="${() => this.setActiveEditIndex(index)}">
                                            <div class="category-name">${topicData.categories
                                                .find((c) => c._id?.$oid === points[0]?.topic_category_id?.$oid)
                                                ?.name}</div>
                                            <div class="place-name"><b>${points.length > 1 ? `${location?.search ?? 'hidden'} (${points.length})` : `${points[0]?.name ?? location?.search ?? 'hidden'}`}</b></div>
                                        </div>
                                        ${this.challengeType === 'hunt' || this.challengeType === 'treasure' || this.challengeType === 'trivia' ? html`
                                            <div class="clue">
                                                ${challengeLocation.is_active ? challengeLocation.clue_details?.clue ?? '' : ''}
                                            </div>
                                            <div class="clue">
                                                ${challengeLocation.is_active ? challengeLocation.clue_details?.hint ?? '' : ''}
                                            </div>
                                        ` : ''}
                                        <div class="point">
                                            <div class="place-name">
                                                ${challengeLocation.is_active && this.challengeType !== 'guide' ? available_methods_string : ''}
                                            </div>
                                        </div>
                                        ${this.challengeType !== 'guide' ? html`
                                            <div class="checkin-value">
                                                ${challengeLocation.is_active ? challengeLocation.value : 0}
                                            </div>
                                        ` : ''}
                                        ${this.challengeCheckInVerificationRequired && this.challengeCodeVerificationEnabled && this.challengeType !== 'guide' ? html`
                                            <div class="checkin-value">
                                                ${challengeLocation.check_in_code === '' || !challengeLocation.is_active ? '' : challengeLocation.check_in_code}
                                            </div>
                                        ` : ''}
                                        ${this.challengeCheckInVerificationRequired && this.challengeQRVerificationEnabled && this.challengeType !== 'guide' ? html`
                                            <div class="confirm-code" ?disabled="${!challengeLocation.is_active}">
                                                ${undefinedIfEmpty(challengeLocation.qr_code_key) && challengeLocation.check_in_code !== '' && challengeLocation.is_active ? html`
                                                    <mwc-icon @click="${async () => await this.downloadQRCodeLink(index)}">qr_code</mwc-icon>
                                                ` : ''}
                                            </div>
                                        ` : ''}
                                        ${this.challengeType !== 'guide' ? html`
                                            <div class="checkin-value">
                                                ${this.activeEditIndex === index ? html`
                                                    <mwc-icon @click="${() => this.setActiveEditIndex(-1)}" style="cursor: pointer;" class="arrow_icon">
                                                        close
                                                    </mwc-icon>
                                                    `
                                                : html`
                                                    <mwc-icon @click="${() => this.setActiveEditIndex(index)}" style="cursor: pointer;" class="arrow_icon">
                                                        edit
                                                    </mwc-icon>
                                                `}
                                            </div>
                                        ` : ''}
                                    </div>
                                `;
                            })}

                        </div>
                    </div>
                </div>
                <div class="modal__footer" slot="header">
                        <form-button
                            size="md"
                            variant="outline"
                            class="action-button aqua-blue"
                            @click="${this.handleClickDownload}"
                        >
                            Download List
                        </form-button>
                        ${this.challengeQRVerificationEnabled && this.challenge && this.challenge?.locations?.length > 0 && this.challengeCheckInVerificationRequired ? html`
                            <form-button
                                size="md"
                                variant="outline"
                                class="action-button aqua-blue"
                                @click="${this.handleClickDownloadQrCodes}"
                            >
                                Print All QR Codes
                            </form-button>
                        ` : ''}
                        ${this.needsSave ? html`
                            <form-button
                                class="action-button aqua-blue"

                                ?disabled="${hasInvalidCheckinCode}"
                                .backgroundColor=${'var(--main-invalid-color)'}
                                @click="${this.handleClickSave}"
                            >
                                Save Location Changes to Challenge
                            </form-button>
                        ` :
                        ''}
                    </div>
            </settings-card>
            ${this.activeEditIndex !== -1 ?
                html`
                    <div class="edit-area">
                        <div class="edit-area-header">
                            <h4>Edit Location Details</h4>
                            <mwc-icon @click="${() => this.setActiveEditIndex(-1)}" style="cursor: pointer;">
                                close
                            </mwc-icon>
                        </div>
                        <b>Location:</b> ${this.challengeLocations[this.activeEditIndex].location?.search} | ${this.challengeLocations[this.activeEditIndex]?.location?.latLng.lat}, ${this.challengeLocations[this.activeEditIndex].location?.latLng.lng}
                        ${this.challengeType === 'hunt' || this.challengeType === 'trivia' || this.challengeType === 'treasure' ? html`
                            <proxi-textarea
                                outlined
                                class="edit__textfield"
                                label="${this.clueName}"
                                @change="${async (e: any) => await this.setChallengeLocationClueDetails(this.activeEditIndex, e.target.value)}"
                                value="${this.activeEditDetails.clue_details?.clue ?? ''}"
                                charcounter
                                maxlength="140">
                            </proxi-textarea>
                            <proxi-textarea
                                outlined
                                class="edit__textfield"
                                label="${this.hintName}"
                                @change="${async (e: any) => await this.setChallengeLocationHintDetails(this.activeEditIndex, e.target.value)}"
                                value="${this.activeEditDetails.clue_details?.hint ?? ''}"
                                charcounter
                                maxlength="140">
                            </proxi-textarea>
                            <h4>Cover Image (Optional)</h4>
                            <p class="info-paragraph">Users will see this with the ${challengeLocationsDisplayName(challengeData.challenge, false, 'lower')}.</p>
                            ${undefinedIfEmpty(this.activeEditDetails.clue_details?.image_url) ? html`
                                <div style="display: flex; gap: 10px; align-items: center;">
                                    <img src="${this.activeEditDetails.clue_details?.image_url}" style="max-width: 80%; max-height: 200px; object-fit: contain;" alt="Clue Image">
                                    <mwc-icon @click="${async () => await this.setChallengeLocationImageUrl(this.activeEditIndex, '')}" style="cursor: pointer;">
                                        delete
                                    </mwc-icon>
                                </div>
                            ` : html`
                                <image-upload-component
                                    autoUpload
                                    .imageUrl="${this.activeEditDetails.clue_details?.image_url ?? ''}"
                                    @retreived-autoupload-url="${async (e: any) => await this.setChallengeLocationImageUrl(this.activeEditIndex, e.detail.imageUrl)}"
                                ></image-upload-component>
                            `}
                        ` : ''}
                        <h4>Point Check In Value</h4>
                        <proxi-textfield
                            class="input__confirm-code"
                            id="checkinValue"
                            type="number"
                            outlined
                            min="0"
                            max="1000"
                            suffix="Points"
                            value="${this.activeEditDetails.value ?? ''}"
                            @change="${async (e: any) => await this.onChangeChallengeLocationValue(e, this.activeEditIndex)}"
                        >
                        </proxi-textfield>

                        ${this.challengeCheckInVerificationRequired ? html`
                            <div class="checkbox-wrapper">
                                <form-checkbox
                                    id="SkipValidation"
                                    name="SkipValidation"
                                    ?checked="${this.activeEditDetails.check_in_code === ''}"
                                    @click="${async () => await this.setChallengeLocationCodeToEmpty(this.activeEditIndex)}"
                                    >
                                </form-checkbox>
                                <span>Skip Validation for this Location</span>
                            </div>
                            ${this.activeEditDetails.check_in_code === '' ? '' : html`
                                ${this.challengeCodeVerificationEnabled ? html`
                                    <h4>Check In Code</h4>
                                    <proxi-textfield
                                        class="input__confirm-code"
                                        id="confirmCode"
                                        type="text"
                                        label=""
                                        placeholder=""
                                        outlined
                                        maxLength="25"
                                        value="${this.activeEditDetails.check_in_code ?? ''}"
                                        @change="${async (e: any) => await this.onChangeChallengeLocationCheckinCode(e, this.activeEditIndex)}"
                                    >
                                    </proxi-textfield>
                                    <p class="info-paragraph">A number, a word, or a short phrase all work — up to 25 characters. Capitalization and extra spaces don't matter when participants type it in.</p>
                                ` : ''}
                                ${this.challengeGeofenceVerificationEnabled ? html`
                                    <h4>Allow GeoFence Check In</h4>
                                    <div class="checkbox-wrapper">
                                        <form-checkbox
                                            id="AllowGeoFenceCheckIn"
                                            name="AllowGeoFenceCheckIn"
                                            ?checked="${this.activeEditDetails.can_geofence_checkin ?? true}"
                                            @click="${async () => await this.onChangeChallengeLocationAllowGeofenceCheckin(this.activeEditIndex)}"
                                            >
                                        </form-checkbox>
                                        <span>GeoFence Check In Enabled for this Location</span>
                                    </div>
                                    ${this.activeEditDetails.can_geofence_checkin ?? true ? html`
                                        <p class="info-paragraph">Users will be able to auto check in to this location by being within <b>${challengeData.challenge?.geofence_radius_m} meters </b> of this location.
                                            You can adjust this radius in the setup tab.</p>
                                        </p>` : ''}
                                ` : ''}
                            `}
                        ` : ''}

                    </div>

                ` : ''
            }
        `;
    }

    get activeEditDetails() {
        return this.challengeLocations[this.activeEditIndex];
    }

    setActiveEditIndex(index: number) {
        this.activeEditIndex = index;
    }


    static get styles() {
        return css`
            :host {
                --modal-width: 800px;
                display: flex;
                gap: 10px;
                overflow: hidden;
            }

            h4 {
                margin-bottom: 5px;
                margin-top: 0;
                color: var(--theme-input-color);
                display: inline-flex;
                --mdc-icon-size: 16px;
            }

            .edit-area {
                display: flex;
                flex-direction: column;
                gap: 10px;
                flex: 2;
                background-color: white;
                padding: 10px;
                border: solid var(--main-sky-color) 8px;
                border-radius: 8px;
                overflow-y: auto;
            }

            .info-paragraph {
                color: var(--theme-input-color);
                margin-top: 0;
                margin-bottom: 0;
            }

            .edit-area-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                border-bottom: 1px solid var(--theme-border-color);
            }

            .modalContent {
                position: relative;
                width: 100%;
                height: auto;
                box-sizing: border-box;
                display: flex;
                flex-direction: column;
                overflow-y: auto;
            }

            .modal__header {
                width: 100%;
                text-align: center;
                padding: 0px 20px 5px;
                color: var(--theme-input-color);
                font-weight: 700;
                box-sizing: border-box;
            }

            .modal__content {
                padding: 20px 0 5px;
                width: 100%;
                box-sizing: border-box;
                flex: 1;
                overflow-y: auto;
            }

            .modal__content::-webkit-scrollbar {
                width: 5px;
            }
            /* Track */
            .modal__content::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }
            /* Handle */
            .modal__content::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .modal__footer {
                display: flex;
                align-items: center;
                justify-content: flex-end;
                padding: 5px 0px;
                box-sizing: border-box;
                gap: 10px;
            }

            .challenges-wrapper {
                width: 100%;
                overflow-y: auto;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: flex-start;
                box-sizing: border-box;
                border-radius: 8px;
            }

            .challenge-row,
            .challenge-row__header {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: space-around;
                box-sizing: border-box;
                gap: 10px;
                padding-top: 10px;
                padding-bottom: 10px;
                font-size: .7rem;
                border: var(--theme-input-border-color) solid 1px;
            }

            .checkbox-wrapper {
                display: flex;
                align-items: center;
                gap: 10px;
            }

            .challenge-row__header {
                background-color: var(--theme-border-color);
                border-bottom: var(--theme-input-border-color) solid 1px;
                text-align: center;
                padding-top: 10px;
                padding-bottom:10px
                border-radius: 8px 8px 0 0;
            }

            // alternate background colors
            .challenge-row:nth-child(odd) {
                background-color: var(--divider-color)
            }

            .participant {
                width: 5%;
                box-sizing: border-box;
                color: #1D1B20;
                display: flex;
                justify-content: center;
            }

            .point {
                width: 30%;
                box-sizing: border-box;
                color: #1D1B20;
                flex: 1;
                text-align: left;
            }

            .clue {
                width: 15%;
                box-sizing: border-box;
                color: #1D1B20;
                text-align: left;
            }

            .checkin-value {
                width: 5%;
                box-sizing: border-box;
                font-size: 12px;
                color: #1D1B20;
                text-align: center;
            }

            .confirm-code {
                width: 5%;
                box-sizing: border-box;
                color: #1D1B20;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
            }

            .active_row {
                background-color: var(--main-sky-color);
            }

            .hidden {
                display: none;
            }

            .input__checkin-value {
                width: 46px;
            }

            .input__confirm-code {
                width: 130px;
            }

            .arrow_icon {
                --mdc-icon-size: 16px;
                cursor: pointer;
            }

            .category-name {
                color: #49454F;
            }

            .place-name {
                color: #1D1B20;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            @media only screen and (max-width: 550px) {
                .modalContent {
                    width: 100%;
                    min-height: 100%;
                    max-height: 100%;
                }

                .terms__desc-wrapper {
                    max-height: 200px;
                }
            }
        `;
    }
}